import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { siteConfig } from "../siteConfig";
import Reveal from "./Reveal";

const { colors, faqs, serviceAreas } = siteConfig;

/* Map centered on the first service area, matching /service-areas */
const mapEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(
  `${serviceAreas[0]}, ${siteConfig.state}`
)}&output=embed`;

/* Background behind the waves — configurable via siteConfig.faqAreasBgImage */
const OUTER_BG  = siteConfig.faqAreasBgImage;
/* Floating section fill color — #F7F5EF (colors.light) */
const FLOAT_CLR = siteConfig.colors.light;

const HomeFAQAreasSection = () => {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div className="relative overflow-hidden">

      {/* ── Outer background (photo or solid brand dark) ── */}
      {OUTER_BG ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${OUTER_BG})` }}
          />
          <div className="absolute inset-0" style={{ background: "rgba(5,15,5,0.60)" }} />
        </>
      ) : (
        <div className="absolute inset-0" style={{ backgroundColor: colors.light }} />
      )}

      {/* ── Floating white section with wave edges ── */}
      <div className="relative z-10">

        {/* Top wave — white fills downward from organic curve */}
        <svg
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          style={{ display: "block", width: "100%", height: "80px" }}
        >
          <path
            d="M0,50 C240,10 480,70 720,38 C960,8 1200,60 1440,35 L1440,80 L0,80 Z"
            fill={FLOAT_CLR}
          />
        </svg>

        {/* Content */}
        <div style={{ backgroundColor: FLOAT_CLR }}>
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-14">
            <div className="grid lg:grid-cols-2 gap-14 items-start">

              {/* ── Col 1: FAQ ── */}
              <Reveal as="div">
                <h2
                  className="font-display font-black mb-7"
                  style={{ fontSize: "clamp(1.75rem,3.5vw,2.75rem)", color: colors.dark }}
                >
                  Frequently Asked Questions
                </h2>

                <div className="divide-y" style={{ borderColor: `${colors.primary}28` }}>
                  {faqs.map((faq, i) => (
                    <div key={i}>
                      <button
                        onClick={() => setOpenIdx(openIdx === i ? null : i)}
                        className="w-full flex items-center justify-between gap-4 text-left py-4"
                      >
                        <span className="font-semibold text-sm leading-snug" style={{ color: colors.dark }}>
                          {faq.q}
                        </span>
                        <ChevronDown
                          className="w-4 h-4 shrink-0 transition-transform duration-300"
                          style={{ color: colors.primary, transform: openIdx === i ? "rotate(180deg)" : "rotate(0deg)" }}
                        />
                      </button>
                      <div
                        className="overflow-hidden transition-all duration-300"
                        style={{ maxHeight: openIdx === i ? "300px" : "0px" }}
                      >
                        <p className="text-gray-500 text-sm leading-relaxed pb-4">{faq.a}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>

              {/* ── Col 2: Service Areas + Map ── */}
              <Reveal as="div" animation="right">
                <h2
                  className="font-display font-black uppercase tracking-wide mb-6 leading-tight"
                  style={{ fontSize: "clamp(1.75rem,3.5vw,2.75rem)", color: colors.dark }}
                >
                  Proudly Serving<br />These Areas
                </h2>

                {/* Area links grid */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 mb-8">
                  {serviceAreas.map((area) => (
                    <Link
                      key={area}
                      to={`/service-areas/${area.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-sm font-semibold transition-colors hover:underline"
                      style={{ color: colors.primaryDark }}
                    >
                      {area}
                    </Link>
                  ))}
                </div>

                {/* Map below the areas list */}
                <div className="rounded-2xl overflow-hidden shadow-md" style={{ height: "220px" }}>
                  <iframe
                    src={mapEmbedUrl}
                    title="Service area map"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              </Reveal>

            </div>
          </div>
        </div>

        {/* Bottom wave — white fills upward from organic curve */}
        <svg
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          style={{ display: "block", width: "100%", height: "80px" }}
        >
          <path
            d="M0,0 L1440,0 L1440,30 C1200,72 960,18 720,50 C480,78 240,22 0,45 Z"
            fill={FLOAT_CLR}
          />
        </svg>

      </div>
    </div>
  );
};

export default HomeFAQAreasSection;
